import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { formatCurrency, formatPercent, getPnLColor } from '../../utils/formatters';

/**
 * MobileStockCard — A compact card layout for stock data on mobile.
 * Replaces table rows on small screens.
 *
 * @param {string} symbol
 * @param {string} [name] — Company name
 * @param {number} [shares]
 * @param {number} [currentPrice]
 * @param {number} [gainLoss]
 * @param {number} [gainLossPercent]
 * @param {string} [signal] — BUY/SELL/HOLD
 * @param {number} [confidence]
 * @param {string} [strategy]
 * @param {function} [onClick]
 * @param {React.ReactNode} [actions] — Optional action buttons
 */
const MobileStockCard = ({
  symbol,
  name,
  shares,
  currentPrice,
  avgPrice,
  currentValue,
  gainLoss,
  gainLossPercent,
  signal,
  confidence,
  strategy,
  orderType,
  orderStatus,
  orderDate,
  totalAmount,
  onClick,
  actions,
}) => {
  const hasGainLoss = gainLoss !== undefined && gainLoss !== null;
  const isPositive = hasGainLoss && parseFloat(gainLoss) >= 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { backgroundColor: 'action.hover' } : {},
      }}
    >
      {/* Top row: Symbol + Price/Value */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {symbol}
          </Typography>
          {name && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {name}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {currentPrice !== undefined && (
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(currentPrice)}
            </Typography>
          )}
          {currentValue !== undefined && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Value: {formatCurrency(currentValue)}
            </Typography>
          )}
          {totalAmount !== undefined && (
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(totalAmount)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Middle row: Details */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {shares !== undefined && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {parseFloat(shares).toFixed(2)} shares
          </Typography>
        )}
        {avgPrice !== undefined && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Avg: {formatCurrency(avgPrice)}
          </Typography>
        )}
        {orderType && (
          <Chip
            label={orderType}
            size="small"
            color={orderType === 'BUY' ? 'success' : 'error'}
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
          />
        )}
        {orderStatus && (
          <Chip
            label={orderStatus}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        )}
        {signal && (
          <Chip
            label={signal}
            size="small"
            color={signal === 'BUY' || signal === 'STRONG_BUY' ? 'success' : signal === 'SELL' || signal === 'STRONG_SELL' ? 'error' : 'default'}
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
          />
        )}
        {confidence !== undefined && confidence !== null && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {(parseFloat(confidence) * 100).toFixed(0)}% conf
          </Typography>
        )}
        {strategy && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {strategy.replace(/_/g, ' ')}
          </Typography>
        )}
        {orderDate && (
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
            {new Date(orderDate).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      {/* Bottom row: Gain/Loss */}
      {hasGainLoss && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: getPnLColor(gainLoss) }}>
            {isPositive ? '+' : ''}{formatCurrency(gainLoss)}
          </Typography>
          {gainLossPercent !== undefined && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: getPnLColor(gainLossPercent) }}>
              {formatPercent(gainLossPercent)}
            </Typography>
          )}
        </Box>
      )}

      {/* Actions */}
      {actions && (
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default MobileStockCard;
